import React, { useMemo } from 'react';
import {
  Tooltip,
  Slider,
  NumberInput,
  Button,
  Anchor,
  Skeleton,
  useMantineColorScheme,
  createStyles,
} from '@mantine/core';
import {
  IconArrowUpRight,
  IconArrowDownRight,
  IconShare,
  IconFileExport,
  IconFlask,
  IconSettings,
  IconArrowRight,
} from '@tabler/icons-react';
import {
  formatMoney,
  formatCount,
  formatPercentage,
  formatDeltaText,
  formatRatio,
  formatTime,
} from '../formatters';
import type {
  MarketingDashboardOverviewData,
  MarketingDashboardDiagnosisData,
  MarketingOverviewCard,
  MarketingPerformanceMetric,
  MarketingSourceDistribution,
  MarketingJourneyStep,
  MarketingTopOpportunity,
  MarketingConstraintData,
  MarketingImpactProjection,
  MarketingWhereToAct,
} from '../dashboardTypes';

// ---------------------------------------------------------------------------
// Type aliases (match names used in sub-components below)
// ---------------------------------------------------------------------------

type OverviewCard = MarketingOverviewCard;
type PerformanceMetric = MarketingPerformanceMetric;
type SourceDistribution = MarketingSourceDistribution;
type JourneyStep = MarketingJourneyStep;
type TopOpportunity = MarketingTopOpportunity;
type ConstraintData = MarketingConstraintData;
type ImpactProjection = MarketingImpactProjection;
type WhereToAct = MarketingWhereToAct;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MarketingDashboardViewProps {
  overviewData: MarketingDashboardOverviewData;
  diagnosisData: MarketingDashboardDiagnosisData;
  primaryGoalName: string;
  costThreshold: number;
  dateRangeLabel: string;
  /** Approx number of days currently selected — drives the <30d warning banner. */
  selectedDayCount?: number;
  /** Number of campaigns selected — drives the goal consistency banner (>1). */
  campaignCount?: number;
  projectionPct: number;
  onProjectionChange: (value: number) => void;
  onNavigateToReports: () => void;
  onNavigateToTests: () => void;
  /** Phase 8: Export PDF action (parent triggers window.print or similar). */
  onExportPdf?: () => void;
  /** Phase 8: Share snapshot action (parent calls API + shows modal). */
  onShareLink?: () => void;
  /** Dashboard is currently fetching data — disable interactive controls. */
  loading?: boolean;
  /** Whether the selected site has any campaigns at all. */
  siteHasCampaigns?: boolean;
  /** Number of active tests on the selected site. */
  activeTestsCount?: number;
  /** Open the dashboard settings panel (for the "Select campaigns" empty state). */
  onOpenSettings?: () => void;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = createStyles((theme) => {
  const dark = theme.colorScheme === 'dark';

  return {
    /* ---- layout ---- */
    root: {
      fontFamily: theme.fontFamily,
      color: dark ? '#E0E0E0' : '#1A1A1A',
      padding: '24px 28px',
    },
    section: {
      marginBottom: 28,
    },
    sectionTitle: {
      fontSize: 16,
      fontWeight: 700,
      marginBottom: 14,
      color: dark ? '#FFFFFF' : '#1A1A1A',
    },

    /* ---- Section 1 — Executive Summary Cards ---- */
    overviewGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: 14,
      [`@media (max-width: ${theme.breakpoints.md}px)`]: {
        gridTemplateColumns: 'repeat(2, 1fr)',
      },
      [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
        gridTemplateColumns: '1fr',
      },
    },
    overviewCard: {
      borderRadius: 10,
      border: `1px solid ${dark ? '#333' : '#E8E8E8'}`,
      backgroundColor: dark ? '#1E1E1E' : '#FAFAFA',
      padding: '18px 20px',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: 6,
    },
    overviewCardAmber: {
      borderColor: 'rgb(245, 158, 11)',
    },
    overviewLabel: {
      fontSize: 11,
      fontWeight: 600,
      textTransform: 'uppercase' as const,
      color: dark ? '#909296' : '#888',
      letterSpacing: 0.4,
    },
    overviewValue: {
      fontSize: 28,
      fontWeight: 700,
      lineHeight: 1.1,
      color: dark ? '#FFFFFF' : '#1A1A1A',
    },
    overviewDelta: {
      fontSize: 12,
      fontWeight: 500,
    },
    overviewSub: {
      fontSize: 12,
      color: dark ? '#909296' : '#999',
    },

    /* ---- Section 2 — Performance Metrics ---- */
    perfRow1: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 14,
      marginBottom: 14,
      [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
        gridTemplateColumns: '1fr',
      },
    },
    perfRow2Wrap: {
      width: '100%',
    },
    perfRow2: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: 14,
      [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
        gridTemplateColumns: '1fr',
      },
    },
    metricCard: {
      borderRadius: 10,
      border: `1px solid ${dark ? '#333' : '#E8E8E8'}`,
      backgroundColor: dark ? '#1E1E1E' : '#FAFAFA',
      padding: '18px 20px',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: 6,
    },

    /* ---- Source Distribution ---- */
    sourceGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 20,
      [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
        gridTemplateColumns: '1fr',
      },
    },
    sourcePanel: {
      borderRadius: 10,
      border: `1px solid ${dark ? '#333' : '#E8E8E8'}`,
      backgroundColor: dark ? '#1E1E1E' : '#FAFAFA',
      padding: '18px 20px',
    },
    sourcePanelTitle: {
      fontSize: 13,
      fontWeight: 600,
      marginBottom: 14,
      color: dark ? '#CCCCCC' : '#444',
    },
    sourceRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 4,
    },
    sourceName: {
      fontSize: 12,
      color: dark ? '#CCC' : '#444',
    },
    sourcePct: {
      fontSize: 12,
      fontWeight: 600,
      color: dark ? '#FFFFFF' : '#1A1A1A',
    },
    sourceBarBg: {
      height: 6,
      borderRadius: 3,
      backgroundColor: dark ? '#2A2A2A' : '#F1F5F9',
      marginBottom: 12,
      overflow: 'hidden',
    },
    sourceBarFill: {
      height: '100%',
      borderRadius: 3,
      backgroundColor: '#4F46E5',
    },
    sourceFooter: {
      fontSize: 12,
      color: dark ? '#909296' : '#AAA',
      marginTop: 10,
    },

    /* ---- Section 3 — Journey Flow / Funnel ---- */
    constraintCallout: {
      backgroundColor: dark ? 'rgb(30, 30, 30)' : '#FAFAFA',
      border: `1px solid rgb(239, 68, 68)`,
      borderRadius: 8,
      padding: '14px 18px',
      marginBottom: 18,
    },
    constraintLabel: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      color: 'rgb(239, 68, 68)',
      letterSpacing: 0.6,
      marginBottom: 4,
    },
    constraintStepName: {
      fontSize: 13,
      fontWeight: 600,
      color: 'rgb(239, 68, 68)',
      marginBottom: 2,
    },
    constraintRate: {
      fontSize: 12,
      color: 'rgb(239, 68, 68)',
      marginBottom: 6,
    },
    constraintBadge: {
      display: 'inline-block',
      backgroundColor: 'transparent',
      color: 'rgb(239, 68, 68)',
      border: '1px solid rgb(239, 68, 68)',
      fontSize: 11,
      fontWeight: 600,
      borderRadius: 4,
      padding: '2px 8px',
    },
    funnelContainer: {
      display: 'flex',
      alignItems: 'stretch',
      gap: 8,
      padding: 16,
      borderRadius: 10,
      backgroundColor: dark ? '#1A1B1E' : '#F5F5F5',
      overflowX: 'auto' as const,
    },
    funnelStepCard: {
      flex: 1,
      minWidth: 140,
      position: 'relative' as const,
      backgroundColor: dark ? '#25262B' : '#FFFFFF',
      border: `1px solid ${dark ? '#373A40' : '#E8E8E8'}`,
      borderRadius: 10,
      padding: '20px 16px 16px',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      textAlign: 'center' as const,
      overflow: 'hidden',
    },
    funnelStepCardConstraint: {
      border: '1px solid rgb(239, 68, 68)',
    },
    funnelAccentBar: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      backgroundColor: 'rgb(239, 68, 68)',
    },
    funnelCardLabel: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      letterSpacing: 0.4,
      color: dark ? '#909296' : '#666',
      marginBottom: 8,
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
    },
    funnelCardValue: {
      fontSize: 26,
      fontWeight: 700,
      lineHeight: 1.1,
      color: dark ? '#FFFFFF' : '#1A1A1A',
    },
    funnelCardSubText: {
      fontSize: 11,
      color: dark ? '#909296' : '#888',
      marginTop: 4,
    },
    funnelOpportunityBadge: {
      marginTop: 10,
      display: 'inline-block',
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      letterSpacing: 0.5,
      color: 'rgb(239, 68, 68)',
      backgroundColor: 'transparent',
      border: '1px solid rgb(239, 68, 68)',
      borderRadius: 4,
      padding: '2px 8px',
    },
    funnelArrowGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      paddingInline: 4,
      color: dark ? '#909296' : '#6B7280',
      flexShrink: 0,
    },
    funnelArrowDelta: {
      fontSize: 11,
      fontWeight: 700,
      color: 'rgb(239, 68, 68)',
    },
    funnelArrowGlyph: {
      fontSize: 18,
      lineHeight: 1,
    },
    funnelLegend: {
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      marginTop: 14,
    },
    legendDot: {
      width: 8,
      height: 8,
      borderRadius: '50%',
      display: 'inline-block',
      marginRight: 4,
    },
    legendText: {
      fontSize: 12,
      color: dark ? '#909296' : '#888',
    },
    funnelFooter: {
      fontSize: 12,
      color: dark ? '#909296' : '#AAA',
      marginTop: 10,
    },

    /* ---- Section 4 — Impact Projection ---- */
    impactSection: {
      borderRadius: 10,
      border: `1px solid ${dark ? '#333' : '#E8E8E8'}`,
      backgroundColor: dark ? '#1E1E1E' : '#FAFAFA',
      padding: '22px 24px',
    },
    impactHeadline: {
      fontSize: 15,
      fontWeight: 700,
      color: dark ? '#FFFFFF' : '#1A1A1A',
      marginBottom: 4,
    },
    impactSub: {
      fontSize: 12,
      color: dark ? '#AAA' : '#666',
      marginBottom: 18,
    },
    impactControlLabel: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      color: dark ? '#909296' : '#888',
      letterSpacing: 0.5,
      marginBottom: 8,
    },
    impactControlRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      flexWrap: 'wrap' as const,
      marginBottom: 20,
    },
    impactCurrentRate: {
      fontSize: 13,
      color: dark ? '#CCC' : '#444',
    },
    impactNumberInput: {
      width: 60,
      '& input': {
        border: '2px solid #7C3AED',
        borderRadius: 6,
        textAlign: 'center' as const,
        fontWeight: 600,
      },
    },
    impactSlider: {
      flex: 1,
      minWidth: 120,
    },
    impactOutputRow: {
      display: 'flex',
      alignItems: 'baseline',
      gap: 10,
      marginBottom: 8,
    },
    impactBigNumber: {
      fontSize: 42,
      fontWeight: 700,
      color: 'rgb(34, 197, 94)',
      lineHeight: 1,
    },
    impactBigLabel: {
      fontSize: 15,
      color: dark ? '#CCC' : '#444',
    },
    impactRevenueRow: {
      fontSize: 20,
      fontWeight: 700,
      color: 'rgb(34, 197, 94)',
      marginBottom: 4,
    },
    impactNoRevenue: {
      fontSize: 12,
      fontStyle: 'italic',
      color: dark ? '#909296' : '#AAA',
      marginBottom: 8,
    },
    impactFooter: {
      fontSize: 12,
      fontWeight: 600,
      color: '#7C3AED',
      marginTop: 10,
    },

    /* ---- Section 5 — Where to Act ---- */
    whereToActGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 14,
      [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
        gridTemplateColumns: '1fr',
      },
    },
    wtaPanel: {
      borderRadius: 10,
      border: `1px solid ${dark ? '#333' : '#E8E8E8'}`,
      backgroundColor: dark ? '#1E1E1E' : '#FAFAFA',
      padding: '18px 20px',
    },
    wtaBadge: {
      display: 'inline-block',
      backgroundColor: dark ? '#4A2810' : '#FED7AA',
      color: dark ? 'rgb(245, 158, 11)' : 'rgb(245, 158, 11)',
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      borderRadius: 4,
      padding: '3px 8px',
      marginBottom: 12,
      letterSpacing: 0.4,
    },
    wtaPanelTitle: {
      fontSize: 14,
      fontWeight: 700,
      color: dark ? '#FFFFFF' : '#1A1A1A',
      marginBottom: 12,
    },

    /* Traffic table */
    trafficTable: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      tableLayout: 'fixed' as const,
      // Spec mobile rule: hide Unique Visitors (col 2) and Cost / Conv. (col 4)
      // on mobile so only Source + Conversion Rate (with status pill) remain.
      [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
        '& th:nth-of-type(2), & td:nth-of-type(2), & th:nth-of-type(4), & td:nth-of-type(4)': {
          display: 'none',
        },
        '& th:nth-of-type(1), & td:nth-of-type(1)': {
          width: '60% !important',
        },
        '& th:nth-of-type(3), & td:nth-of-type(3)': {
          width: '40% !important',
        },
      },
    },
    trafficTh: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      color: dark ? '#909296' : '#AAA',
      borderBottom: `1px solid ${dark ? '#333' : '#EEE'}`,
      padding: '6px 4px',
      textAlign: 'left' as const,
      letterSpacing: 0.4,
    },
    trafficTd: {
      fontSize: 12,
      padding: '8px 4px',
      borderBottom: `1px solid ${dark ? '#2A2A2A' : '#F5F5F5'}`,
      color: dark ? '#CCC' : '#333',
      verticalAlign: 'middle' as const,
    },
    trafficRowWorst: {
      borderLeft: '3px solid rgb(239, 68, 68)',
    },
    statusDot: {
      width: 7,
      height: 7,
      borderRadius: '50%',
      display: 'inline-block',
      marginRight: 4,
      verticalAlign: 'middle',
    },
    statusBadge: {
      fontSize: 11,
      fontWeight: 600,
      padding: '1px 5px',
      borderRadius: 3,
      marginLeft: 4,
      display: 'inline-block',
      verticalAlign: 'middle',
    },
    trafficFooter: {
      fontSize: 12,
      color: dark ? '#909296' : '#AAA',
      marginTop: 10,
    },

    /* Experience panel */
    experienceGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 120px',
      rowGap: 10,
      columnGap: 8,
      marginBottom: 14,
    },
    expLabel: {
      fontSize: 12,
      color: dark ? '#AAA' : '#666',
    },
    expValue: {
      fontSize: 13,
      fontWeight: 600,
      textAlign: 'right' as const,
      color: dark ? '#FFFFFF' : '#1A1A1A',
    },
    expPrimaryIssue: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      color: dark ? '#909296' : '#AAAAAA',
      letterSpacing: 0.5,
      marginBottom: 8,
    },
    expCallout: {
      backgroundColor: dark ? '#3D2B10' : '#FFF7ED',
      border: `1px solid ${dark ? '#7C5A26' : '#FED7AA'}`,
      borderRadius: 6,
      padding: '12px 14px',
    },
    expCalloutLabel: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      color: dark ? 'rgb(245, 158, 11)' : 'rgb(245, 158, 11)',
      letterSpacing: 0.4,
      marginBottom: 4,
    },
    expCalloutText: {
      fontSize: 12,
      color: dark ? '#E0E0E0' : '#444',
      lineHeight: 1.5,
    },

    /* ---- Section 6 — Top Opportunities ---- */
    opGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      // Spec: all rendered cards must be equal height regardless of content
      // length. `align-items: stretch` forces every card to fill the tallest
      // row height so titles/descriptions of different sizes line up.
      alignItems: 'stretch' as const,
      gap: 14,
      [`@media (max-width: ${theme.breakpoints.sm}px)`]: {
        gridTemplateColumns: '1fr',
      },
    },
    opCard: {
      borderRadius: 10,
      padding: '18px 20px',
      display: 'flex',
      flexDirection: 'column' as const,
    },
    opCardPrimary: {
      border: '1.5px solid #7C3AED',
      backgroundColor: dark ? '#1D1A26' : '#FDFCFF',
    },
    opCardSecondary: {
      border: `1px solid ${dark ? '#333' : '#E8E8E8'}`,
      backgroundColor: dark ? '#1E1E1E' : '#FAFAFA',
    },
    opPriority: {
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      color: dark ? '#909296' : '#888',
      letterSpacing: 0.5,
      marginBottom: 6,
    },
    opTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: dark ? '#FFFFFF' : '#1A1A1A',
      marginBottom: 10,
    },
    opDivider: {
      height: 1,
      marginBottom: 10,
    },
    opDividerPrimary: {
      backgroundColor: dark ? '#3D3459' : '#F0ECFF',
    },
    opDividerSecondary: {
      backgroundColor: dark ? '#333' : '#F0F0F0',
    },
    opDescription: {
      fontSize: 12,
      color: dark ? '#AAA' : '#666',
      lineHeight: 1.5,
      flex: 1,
      marginBottom: 10,
    },
    opCta: {
      fontSize: 12,
      fontWeight: 600,
      color: '#4F46E5',
      cursor: 'pointer',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    },

    /* ---- Footer ---- */
    footer: {
      borderTop: `1px solid ${dark ? '#333' : '#F0F0F0'}`,
      padding: '16px 28px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap' as const,
      gap: 10,
    },
    footerText: {
      fontSize: 12,
      color: dark ? '#909296' : '#AAAAAA',
    },
    footerLink: {
      color: dark ? '#818CF8' : '#4F46E5',
      fontSize: 12,
    },
    footerButtons: {
      display: 'flex',
      gap: 8,
    },

    /* ---- Empty states ---- */
    emptyHero: {
      borderRadius: 12,
      border: `1px solid ${dark ? '#333' : '#E8E8E8'}`,
      background: dark ? '#1E1E1E' : '#FAFAFA',
      padding: '28px 32px',
      display: 'flex',
      gap: 18,
      alignItems: 'center',
      flexWrap: 'wrap' as const,
    },
    emptyIcon: {
      width: 52, height: 52, borderRadius: '50%',
      background: dark ? '#2D2545' : '#EEF2FF',
      color: dark ? '#A78BFA' : '#4F46E5',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    },
    emptyTitle: { fontSize: 16, fontWeight: 700, color: dark ? '#FFFFFF' : '#1A1A1A', margin: 0 },
    emptyBody: { fontSize: 13, color: dark ? '#C1C2C5' : '#555', marginTop: 4, lineHeight: 1.5 },
    emptyMessage: {
      borderRadius: 10,
      border: `1px solid ${dark ? '#333' : '#E8E8E8'}`,
      background: dark ? '#1E1E1E' : '#FAFAFA',
      padding: '18px 22px',
      marginBottom: 14,
    },
    emptyMessageTitle: { fontSize: 14, fontWeight: 600, color: dark ? '#FFFFFF' : '#1A1A1A', margin: 0 },
    emptyMessageSub: { fontSize: 13, color: dark ? '#909296' : '#6B7280', marginTop: 6, lineHeight: 1.5 },
    emptyTestsRow: {
      border: `1px solid ${dark ? '#333' : '#E8E8E8'}`,
      borderRadius: 10,
      padding: '14px 18px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap' as const,
      gap: 10,
    },
    emptyTestsLeft: { fontSize: 13, color: dark ? '#C1C2C5' : '#666' },
    emptyTestsRight: {
      fontSize: 12,
      color: dark ? '#818CF8' : '#4F46E5',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
    },
  };
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtValue(value: number | null, format: string): string {
  if (value === null || value === undefined) return '\u2014';
  switch (format) {
    case 'money':
      return formatMoney(value);
    case 'money_signed': {
      // Revenue vs Spend Gap — always render with an explicit +/− sign.
      const sign = value >= 0 ? '+' : '−';
      return `${sign}${formatMoney(Math.abs(value))}`;
    }
    case 'count':
      return formatCount(value);
    case 'percentage':
      return formatPercentage(value);
    case 'ratio':
      return formatRatio(value);
    default:
      return String(value);
  }
}

function deltaColor(pct: number | null): string {
  if (pct === null || pct === undefined) return '#AAA';
  return pct >= 0 ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)';
}

function deltaIcon(pct: number | null, size = 14) {
  if (pct === null || pct === undefined) return null;
  return pct >= 0 ? (
    <IconArrowUpRight size={size} color="rgb(34, 197, 94)" />
  ) : (
    <IconArrowDownRight size={size} color="rgb(239, 68, 68)" />
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/* ---- Section 1 — Executive Summary ---- */
const ExecutiveSummaryCards: React.FC<{
  cards: OverviewCard[];
  costThreshold: number;
  isDark: boolean;
  classes: ReturnType<typeof useStyles>['classes'];
}> = ({ cards, costThreshold, isDark, classes }) => {
  return (
    <div className={classes.section}>
      <div className={classes.overviewGrid}>
        {cards.map((card, i) => {
          const isAmber =
            card.label.toLowerCase().includes('cost per') &&
            card.benchmark != null &&
            card.value != null &&
            card.value > card.benchmark;

          const isRoas = card.label.toLowerCase().includes('roas');
          const displayValue =
            isRoas && card.value === null ? '\u2014' : fmtValue(card.value, card.format);

          const cardEl = (
            <div
              key={i}
              className={`${classes.overviewCard} ${isAmber ? classes.overviewCardAmber : ''}`}
              style={isAmber ? { borderColor: 'rgb(245, 158, 11)' } : undefined}
            >
              <span className={classes.overviewLabel}>{card.label}</span>
              <span
                className={classes.overviewValue}
                style={isAmber ? { color: 'rgb(245, 158, 11)' } : undefined}
              >
                {displayValue}
              </span>
              {card.delta_pct !== null && card.delta_pct !== undefined && (
                <span
                  className={classes.overviewDelta}
                  style={{ color: deltaColor(card.delta_pct) }}
                >
                  {deltaIcon(card.delta_pct)} {formatDeltaText(card.delta_pct, 'vs previous period')}
                </span>
              )}
              {isAmber && card.benchmark != null && (
                <span className={classes.overviewSub}>
                  Benchmark: {formatMoney(card.benchmark)}
                </span>
              )}
            </div>
          );

          if (isRoas && card.value === null) {
            return (
              <Tooltip key={i} label="Revenue data is not yet available" withArrow>
                {cardEl}
              </Tooltip>
            );
          }
          return cardEl;
        })}
      </div>
    </div>
  );
};

/* ---- Section 2 — Performance Metrics ---- */
const PerformanceMetrics: React.FC<{
  primary: PerformanceMetric[];
  secondary: PerformanceMetric[];
  isDark: boolean;
  classes: ReturnType<typeof useStyles>['classes'];
}> = ({ primary, secondary, isDark, classes }) => {
  const renderCard = (m: PerformanceMetric, idx: number) => {
    const isRevenue = m.label.toLowerCase().includes('revenue');
    const val = isRevenue && m.value === null ? '\u2014' : fmtValue(m.value, m.format);

    // Revenue vs Spend Gap (format='money_signed') — colour the value green
    // when positive, red when negative, neutral only when dashed.
    const isSigned = m.format === 'money_signed';
    const signedColor = isSigned && m.value != null
      ? (m.value >= 0 ? (isDark ? 'rgb(34, 197, 94)' : 'rgb(34, 197, 94)') : (isDark ? 'rgb(239, 68, 68)' : 'rgb(239, 68, 68)'))
      : undefined;

    const inner = (
      <div key={idx} className={classes.metricCard}>
        <span className={classes.overviewLabel}>{m.label}</span>
        <span className={classes.overviewValue} style={signedColor ? { color: signedColor } : undefined}>{val}</span>
        {m.delta_pct !== null && m.delta_pct !== undefined && (
          <span className={classes.overviewDelta} style={{ color: deltaColor(m.delta_pct) }}>
            {deltaIcon(m.delta_pct)} {formatDeltaText(m.delta_pct, 'vs previous period')}
          </span>
        )}
      </div>
    );

    if (isRevenue && m.value === null) {
      return (
        <Tooltip key={idx} label="Revenue data not yet available" withArrow>
          {inner}
        </Tooltip>
      );
    }
    return inner;
  };

  return (
    <div className={classes.section}>
      <div className={classes.sectionTitle}>Performance Metrics</div>
      <div className={classes.perfRow1}>
        {primary.map((m, i) => renderCard(m, i))}
      </div>
      <div className={classes.perfRow2Wrap}>
        <div className={classes.perfRow2}>
          {secondary.map((m, i) => renderCard(m, i + 100))}
        </div>
      </div>
    </div>
  );
};

/* ---- Source Distribution ---- */
const SourceDistributionSection: React.FC<{
  sources: SourceDistribution[];
  goalName: string;
  isDark: boolean;
  classes: ReturnType<typeof useStyles>['classes'];
}> = ({ sources, goalName, isDark, classes }) => {
  const items = sources.slice(0, 6);

  const renderBars = (field: 'visitor_pct' | 'conversion_pct') => {
    const maxVal = Math.max(...items.map((s) => s[field]), 1);
    return items.map((s, i) => (
      <div key={i}>
        <div className={classes.sourceRow}>
          <span className={classes.sourceName}>{s.source_name}</span>
          <span className={classes.sourcePct}>{formatPercentage(s[field], 1)}</span>
        </div>
        <div className={classes.sourceBarBg}>
          <div
            className={classes.sourceBarFill}
            style={{ width: `${(s[field] / maxVal) * 100}%` }}
          />
        </div>
      </div>
    ));
  };

  return (
    <div className={classes.section}>
      <div className={classes.sectionTitle}>Source Distribution</div>
      <div className={classes.sourceGrid}>
        <div className={classes.sourcePanel}>
          <div className={classes.sourcePanelTitle}>
            Traffic source distribution &mdash; unique visitors
          </div>
          {renderBars('visitor_pct')}
        </div>
        <div className={classes.sourcePanel}>
          <div className={classes.sourcePanelTitle}>
            Traffic source distribution &mdash; {goalName} conversions
          </div>
          {renderBars('conversion_pct')}
        </div>
      </div>
      <div className={classes.sourceFooter}>
        Percentages reflect share of total within the selected date range. Sources with minimal traffic may be grouped.
      </div>
    </div>
  );
};

/* ---- Section 3 — Customer Journey Flow ---- */
const JourneyFlowSection: React.FC<{
  steps: JourneyStep[];
  constraint: ConstraintData;
  isDark: boolean;
  classes: ReturnType<typeof useStyles>['classes'];
  onNavigateToReports: () => void;
}> = ({ steps, constraint, isDark, classes, onNavigateToReports }) => {

  // Whether ANY step has a benchmark loaded — drives the footer prompt only
  // (section still renders without benchmarks, per spec).
  const anyBenchmarkPresent = steps.some((s) => s.benchmark_rate != null && (s.benchmark_rate as number) > 0);

  // Highlight the weakest step directly in the funnel — pick the step
  // (skipping the entry) with the lowest progression rate, same way the
  // Business Dashboard does it. Computed locally so it works even when the
  // backend has no benchmarks loaded.
  let highlightedIdx = -1;
  {
    let lowestRate = Infinity;
    steps.forEach((s, idx) => {
      if (idx === 0) return;
      const r = s.conversion_rate;
      if (r != null && (r as number) < lowestRate) {
        lowestRate = r as number;
        highlightedIdx = idx;
      }
    });
  }

  return (
    <div className={classes.section}>
      <div className={classes.sectionTitle}>Customer Journey Flow</div>

      {!anyBenchmarkPresent && (
        <div style={{
          background: isDark ? '#25262B' : '#FAFAFA',
          border: `1px solid ${isDark ? '#373A40' : '#E8E8E8'}`,
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 14,
          fontSize: 12,
          color: isDark ? '#C1C2C5' : '#6B7280',
          lineHeight: 1.5,
        }}>
          Add benchmarks to your Campaign Report to enable Primary Constraint detection.
        </div>
      )}

      <div className={classes.funnelContainer}>
        {steps.map((step, i) => {
          const isFirst = i === 0;
          const isHighlighted = i === highlightedIdx;

          // Drop % shown between cards = 100 - progression%
          const dropPct =
            step.conversion_rate != null
              ? Math.max(0, 100 - (step.conversion_rate as number))
              : null;
          const dropLabel =
            dropPct != null
              ? `-${dropPct.toFixed(dropPct < 10 ? 1 : 0)}%`
              : '';

          // Inside each card, show progression % (except for the entry step).
          const progressionInside =
            !isFirst && step.conversion_rate != null
              ? `${(step.conversion_rate as number).toFixed((step.conversion_rate as number) < 10 ? 2 : 1)}% of previous step`
              : '';

          return (
            <React.Fragment key={i}>
              {i > 0 && (
                <div className={classes.funnelArrowGroup}>
                  {dropLabel && <span className={classes.funnelArrowDelta}>{dropLabel}</span>}
                  <span className={classes.funnelArrowGlyph}>{'›'}</span>
                </div>
              )}
              <div
                className={`${classes.funnelStepCard} ${isHighlighted ? classes.funnelStepCardConstraint : ''}`}
              >
                {isHighlighted && <div className={classes.funnelAccentBar} />}
                <div className={classes.funnelCardLabel}>{step.name}</div>
                <div className={classes.funnelCardValue}>{formatCount(step.users)}</div>
                <div className={classes.funnelCardSubText}>
                  {isFirst ? 'Visitors' : 'Conversions'}
                </div>
                {progressionInside && (
                  <div className={classes.funnelCardSubText}>{progressionInside}</div>
                )}
                {isHighlighted && (
                  <span className={classes.funnelOpportunityBadge}>Biggest Opportunity</span>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <div className={classes.funnelFooter}>
        All goals shown &middot; Progression % per step &middot;{' '}
        <Anchor
          component="button"
          size="xs"
          style={{ color: isDark ? '#818CF8' : '#4F46E5', fontSize: 12 }}
          onClick={onNavigateToReports}
        >
          View Lifecycle Report &rarr;
        </Anchor>
      </div>
    </div>
  );
};

/* ---- Section 4 — Impact Projection ---- */
const ImpactProjectionSection: React.FC<{
  constraint: ConstraintData;
  projection: ImpactProjection;
  projectionPct: number;
  onProjectionChange: (v: number) => void;
  isDark: boolean;
  classes: ReturnType<typeof useStyles>['classes'];
  loading?: boolean;
}> = ({ constraint, projection, projectionPct, onProjectionChange, isDark, classes, loading = false }) => {
  // Calculate additional conversions based on slider
  const currentRate = projection?.current_rate ?? 0;
  const usersAtStep = projection?.users_at_step ?? 0;
  const targetRate = projectionPct;
  const rateDiff = Math.max(targetRate - currentRate, 0);
  const additionalConversions =
    usersAtStep > 0 ? Math.round((rateDiff / 100) * usersAtStep) : 0;
  const additionalRevenue =
    projection?.additional_revenue != null && (projection?.additional_conversions ?? 0) > 0
      ? (additionalConversions / projection.additional_conversions) *
        projection.additional_revenue
      : null;

  const impactMode = constraint?.mode ?? (constraint?.benchmark_rate != null ? 'primary_constraint' : 'biggest_dropoff');
  const impactSubText = impactMode === 'primary_constraint'
    ? 'Based on your primary constraint'
    : 'Based on the largest drop-off in your funnel';

  return (
    <div className={classes.section}>
      <div className={classes.sectionTitle}>Impact of Improving This Step</div>
      <div className={classes.impactSection}>
        <div className={classes.impactHeadline}>
          Improving {constraint?.from_step ?? ''} &rarr; {constraint?.to_step ?? ''} will drive the highest revenue impact
        </div>
        <div className={classes.impactSub}>{impactSubText}</div>

        {/* Controls */}
        <div className={classes.impactControlLabel}>Simulate improvement in this step</div>
        <div className={classes.impactControlRow} data-print-hide>
          <span className={classes.impactCurrentRate}>
            Current rate: {formatPercentage(currentRate)}
          </span>
          <span style={{ color: isDark ? '#CCC' : '#444', fontSize: 13 }}>Target:</span>
          <NumberInput
            value={projectionPct}
            onChange={(val) => onProjectionChange(typeof val === 'number' ? val : currentRate)}
            min={currentRate}
            max={100}
            step={0.1}
            precision={1}
            hideControls
            disabled={loading}
            className={classes.impactNumberInput}
            styles={{
              input: {
                border: '2px solid #7C3AED',
                borderRadius: 6,
                textAlign: 'center',
                fontWeight: 600,
              },
            }}
          />
          <div
            className={classes.impactSlider}
            style={{
              pointerEvents: loading ? 'none' : 'auto',
              opacity: loading ? 0.6 : 1,
              transition: 'opacity 120ms ease',
            }}
          >
            <Slider
              value={projectionPct}
              onChange={onProjectionChange}
              min={currentRate}
              max={Math.min((projection?.benchmark_rate ?? 10) * 2, 100)}
              step={0.1}
              label={(v) => `${v.toFixed(1)}%`}
              disabled={loading}
              styles={{
                bar: { backgroundColor: '#7C3AED' },
                thumb: { borderColor: '#7C3AED', backgroundColor: '#7C3AED' },
              }}
            />
          </div>
        </div>

        {/* Output */}
        <div className={classes.impactOutputRow}>
          <span className={classes.impactBigNumber}>+{formatCount(additionalConversions)}</span>
          <span className={classes.impactBigLabel}>
            Additional {projection?.primary_goal_name || 'conversions'}
          </span>
        </div>

        {additionalRevenue !== null ? (
          <div className={classes.impactRevenueRow}>+{formatMoney(additionalRevenue)} additional revenue</div>
        ) : (
          <div className={classes.impactNoRevenue}>
            Revenue will appear once revenue data is available via webhook
          </div>
        )}

        <div style={{
          fontSize: 12,
          color: isDark ? '#C1C2C5' : '#6B7280',
          marginTop: 10,
          lineHeight: 1.5,
        }}>
          Based on current traffic levels
        </div>

        {/* Spec: permanent disclaimer — non-dismissible, never hidden. */}
        <div style={{
          fontSize: 12,
          color: isDark ? '#909296' : '#9CA3AF',
          fontStyle: 'italic',
          marginTop: 6,
          lineHeight: 1.5,
        }}>
          Projections will vary based on the amount of data available to Visiopt.
        </div>

        {/* Spec: purple footer line */}
        <div className={classes.impactFooter} style={{ color: isDark ? '#A78BFA' : '#7C3AED', fontWeight: 600, fontSize: 12, marginTop: 8 }}>
          This is the highest leverage improvement in your funnel
        </div>
      </div>
    </div>
  );
};

/* ---- Section 5 — Where to Act ---- */
const WhereToActSection: React.FC<{
  constraint: ConstraintData;
  whereToAct: WhereToAct;
  isDark: boolean;
  classes: ReturnType<typeof useStyles>['classes'];
}> = ({ constraint, whereToAct, isDark, classes }) => {
  const badgeLabel = `AT CONSTRAINT STEP: ${constraint?.from_step ?? ''} \u2192 ${constraint?.to_step ?? ''}`;
  const exp = whereToAct?.experience;
  const trafficRows = whereToAct?.traffic_rows ?? [];

  if (!exp) return null;

  const convBelowBenchmark = (exp.conversion_rate ?? 0) < (exp.benchmark_rate ?? 0);

  return (
    <div className={classes.section}>
      <div className={classes.sectionTitle}>What&rsquo;s Causing the Drop-Off</div>
      <div className={classes.whereToActGrid}>
        {/* Left: Traffic Quality (was "Traffic Impact") */}
        <div className={classes.wtaPanel}>
          <span className={classes.wtaBadge}>{badgeLabel}</span>
          <div className={classes.wtaPanelTitle}>Traffic Quality</div>
          <table className={classes.trafficTable}>
            <thead>
              <tr>
                <th className={classes.trafficTh} style={{ width: '36%' }}>
                  Source
                </th>
                <th className={classes.trafficTh} style={{ width: '18%' }}>
                  Unique Visitors
                </th>
                <th className={classes.trafficTh} style={{ width: '28%' }}>
                  Conversion Rate
                </th>
                <th className={classes.trafficTh} style={{ width: '18%' }}>
                  Cost / Conv.
                </th>
              </tr>
            </thead>
            <tbody>
              {trafficRows.map((row, i) => {
                const isBelow = (row.conversion_rate ?? 0) < (row.benchmark_rate ?? 0);
                return (
                  <tr key={i} className={row.is_worst ? classes.trafficRowWorst : ''}>
                    <td className={classes.trafficTd}>{row.source_name}</td>
                    <td className={classes.trafficTd}>{formatCount(row.unique_visitors)}</td>
                    <td className={classes.trafficTd}>
                      <span
                        className={classes.statusDot}
                        style={{ backgroundColor: isBelow ? 'rgb(239, 68, 68)' : 'rgb(34, 197, 94)' }}
                      />
                      {formatPercentage(row.conversion_rate)}
                      <span
                        className={classes.statusBadge}
                        style={{
                          backgroundColor: isBelow
                            ? isDark
                              ? '#4A1515'
                              : '#FEE2E2'
                            : isDark
                            ? '#0F3D1A'
                            : '#DCFCE7',
                          color: isBelow ? 'rgb(239, 68, 68)' : 'rgb(34, 197, 94)',
                        }}
                      >
                        {isBelow ? 'Below' : 'Above'}
                      </span>
                    </td>
                    <td className={classes.trafficTd}>
                      {row.cost_per_conversion !== null
                        ? formatMoney(row.cost_per_conversion)
                        : '\u2014'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className={classes.trafficFooter}>Aggregated across selected campaigns.</div>
        </div>

        {/* Right: On-Page Experience (was "Experience Impact") */}
        <div className={classes.wtaPanel}>
          <span className={classes.wtaBadge}>{badgeLabel}</span>
          <div className={classes.wtaPanelTitle}>On-Page Experience</div>
          <div className={classes.experienceGrid}>
            <span className={classes.expLabel}>Conversion rate</span>
            <span
              className={classes.expValue}
              style={{ color: convBelowBenchmark ? 'rgb(239, 68, 68)' : isDark ? '#FFFFFF' : '#1A1A1A' }}
            >
              {formatPercentage(exp.conversion_rate)}{' '}
              <span style={{ fontWeight: 400, fontSize: 12, color: isDark ? '#909296' : '#AAA' }}>
                (vs {formatPercentage(exp.benchmark_rate)})
              </span>
            </span>

            <span className={classes.expLabel}>Avg time to next step</span>
            <span className={classes.expValue}>
              {exp.avg_time_hours !== null ? formatTime(exp.avg_time_hours) : '\u2014'}
            </span>

            <span className={classes.expLabel}>Revenue per visitor</span>
            {exp.revenue_per_visitor !== null ? (
              <span className={classes.expValue}>{formatMoney(exp.revenue_per_visitor)}</span>
            ) : (
              <Tooltip label="Revenue data not yet available" withArrow>
                <span
                  className={classes.expValue}
                  style={{ color: isDark ? '#555' : '#CCC', cursor: 'help' }}
                >
                  &mdash;
                </span>
              </Tooltip>
            )}
          </div>

          <div className={classes.expCallout}>
            <div className={classes.expCalloutLabel}>
              {whereToAct?.primary_issue || 'What This Suggests'}
            </div>
            <div className={classes.expCalloutText}>{whereToAct?.interpretation || 'Select campaigns and configure benchmarks to see diagnosis.'}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---- Section 6 — Top Opportunities ---- */
const TopOpportunitiesSection: React.FC<{
  opportunities: TopOpportunity[];
  isDark: boolean;
  classes: ReturnType<typeof useStyles>['classes'];
  onNavigateToReports: () => void;
  onNavigateToTests: () => void;
}> = ({ opportunities, isDark, classes, onNavigateToReports, onNavigateToTests }) => {
  const items = opportunities.slice(0, 3);

  return (
    <div className={classes.section}>
      <div className={classes.sectionTitle}>Top Opportunities</div>
      <div className={classes.opGrid}>
        {items.map((op, i) => {
          const isPrimary = op.priority === 1;
          return (
            <div
              key={i}
              className={`${classes.opCard} ${isPrimary ? classes.opCardPrimary : classes.opCardSecondary}`}
            >
              <div className={classes.opPriority}>Priority {op.priority}</div>
              <div className={classes.opTitle}>{op.title}</div>
              <div
                className={`${classes.opDivider} ${isPrimary ? classes.opDividerPrimary : classes.opDividerSecondary}`}
              />
              <div className={classes.opDescription}>{op.description}</div>
              <Anchor
                component="button"
                className={classes.opCta}
                onClick={
                  op.cta_action === 'tests' || op.cta_action === 'test_results'
                    ? onNavigateToTests
                    : onNavigateToReports
                }
              >
                {op.cta_label}
              </Anchor>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ---- Footer ---- */
const DashboardFooter: React.FC<{
  dateRangeLabel: string;
  isDark: boolean;
  classes: ReturnType<typeof useStyles>['classes'];
  onExportPdf?: () => void;
  onShareLink?: () => void;
}> = ({ dateRangeLabel, isDark, classes, onExportPdf, onShareLink }) => {
  return (
    <div className={classes.footer}>
      <span className={classes.footerText}>
        Data reflects {dateRangeLabel.toLowerCase()}.
      </span>
      <div className={classes.footerButtons} data-print-hide>
        <Button
          variant="outline"
          size="xs"
          leftIcon={<IconShare size={14} />}
          onClick={onShareLink}
          styles={{
            root: {
              borderColor: isDark ? '#444' : '#DDD',
              color: isDark ? '#CCC' : '#444',
            },
          }}
        >
          Share link
        </Button>
        <Button
          size="xs"
          leftIcon={<IconFileExport size={14} />}
          onClick={onExportPdf}
          styles={{
            root: {
              backgroundColor: '#4F46E5',
              '&:hover': { backgroundColor: '#4338CA' },
            },
          }}
        >
          Export PDF
        </Button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const MarketingDashboardView: React.FC<MarketingDashboardViewProps> = ({
  overviewData,
  diagnosisData,
  primaryGoalName,
  costThreshold,
  dateRangeLabel,
  selectedDayCount = 30,
  campaignCount = 0,
  projectionPct,
  onProjectionChange,
  onNavigateToReports,
  onNavigateToTests,
  onExportPdf,
  onShareLink,
  loading = false,
  siteHasCampaigns = true,
  activeTestsCount = 0,
  onOpenSettings,
}) => {
  const { classes } = useStyles();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  // Safe-access helpers — all data may be undefined on first render
  // overview_cards comes from the API as a flat object { ad_spend, primary_goal_conversions, ... }
  // Transform it into the OverviewCard[] array the sub-components expect.
  const rawCards = overviewData?.overview_cards;
  const cards: OverviewCard[] = useMemo(() => {
    if (!rawCards || typeof rawCards !== 'object') return [];
    // If the API already returns an array, use it directly
    if (Array.isArray(rawCards)) return rawCards;
    // Otherwise build from the flat object the API returns
    const oc = rawCards as Record<string, any>;
    const goalName = oc.primary_goal_name || primaryGoalName || 'Primary Goal';
    return [
      { label: 'Ad Spend', value: oc.ad_spend ?? null, format: 'money' as const, delta_pct: overviewData?.deltas?.ad_spend ?? null },
      { label: goalName, value: oc.primary_goal_conversions ?? null, format: 'count' as const, delta_pct: overviewData?.deltas?.primary_goal_conversions ?? null },
      { label: `Cost per ${goalName}`, value: oc.cost_per_goal ?? null, format: 'money' as const, delta_pct: overviewData?.deltas?.cost_per_goal ?? null, benchmark: costThreshold > 0 ? costThreshold : undefined },
      { label: 'Total ROAS', value: oc.roas ?? null, format: 'ratio' as const, delta_pct: overviewData?.deltas?.roas ?? null, tooltip: oc.roas == null ? 'Calculated when revenue data is detected via webhook' : undefined },
    ];
  }, [rawCards, overviewData?.deltas, primaryGoalName, costThreshold]);
  const perfPrimary = overviewData?.performance_metrics?.primary ?? [];
  const perfSecondary = overviewData?.performance_metrics?.secondary ?? [];
  const sources = overviewData?.source_distribution ?? [];
  const journeySteps = overviewData?.journey_flow ?? [];
  const constraint = diagnosisData?.constraint;
  const projection = diagnosisData?.impact_projection;
  const whereToAct = diagnosisData?.where_to_act;
  // Top opportunities come from phase6 (diagnosis), not phase5 (overview)
  const opportunities = diagnosisData?.top_opportunities ?? overviewData?.top_opportunities ?? [];
  const skippedCampaigns = overviewData?.warnings?.skipped_campaigns ?? [];

  // First-load skeleton — only when fetching AND we don't have data yet.
  // On subsequent fetches (filter changes, etc.) we keep the previous data
  // visible because the parent already shows VisiLoader for those.
  const hasNoData =
    !overviewData ||
    (cards.length === 0 && perfPrimary.length === 0 && perfSecondary.length === 0);

  if (loading && hasNoData) {
    return (
      <div className={classes.root}>
        {/* Section 1 — Executive Summary (4 cards) */}
        <div className={classes.section}>
          <Skeleton height={16} width={210} radius="sm" mb={14} />
          <div className={classes.overviewGrid}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={classes.overviewCard}>
                <Skeleton height={11} width="55%" radius="sm" mb={8} />
                <Skeleton height={28} width="65%" radius="sm" mb={8} />
                <Skeleton height={11} width="70%" radius="sm" />
              </div>
            ))}
          </div>
        </div>

        {/* Section 2 — Performance Metrics */}
        <div className={classes.section}>
          <Skeleton height={16} width={200} radius="sm" mb={14} />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 14,
            }}
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                style={{
                  borderRadius: 10,
                  border: `1px solid ${isDark ? '#333' : '#E8E8E8'}`,
                  backgroundColor: isDark ? '#1E1E1E' : '#FAFAFA',
                  padding: '16px 18px',
                }}
              >
                <Skeleton height={11} width="60%" radius="sm" mb={10} />
                <Skeleton height={22} width="55%" radius="sm" />
              </div>
            ))}
          </div>
        </div>

        {/* Source Distribution */}
        <div className={classes.section}>
          <Skeleton height={16} width={180} radius="sm" mb={14} />
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr 1fr',
                gap: 12,
                padding: '12px 16px',
                borderBottom: `1px solid ${isDark ? '#2a2a2a' : '#F0F0F0'}`,
              }}
            >
              <Skeleton height={12} width="70%" radius="sm" />
              <Skeleton height={12} width="55%" radius="sm" style={{ marginLeft: 'auto' }} />
              <Skeleton height={12} width="45%" radius="sm" style={{ marginLeft: 'auto' }} />
              <Skeleton height={12} width="55%" radius="sm" style={{ marginLeft: 'auto' }} />
            </div>
          ))}
        </div>

        {/* Customer Journey Flow */}
        <div className={classes.section}>
          <Skeleton height={16} width={220} radius="sm" mb={14} />
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton
                key={i}
                height={88}
                width={140}
                radius="md"
              />
            ))}
          </div>
        </div>

        {/* Impact of Improving This Step */}
        <div className={classes.section}>
          <Skeleton height={16} width={260} radius="sm" mb={14} />
          <div
            style={{
              border: `1px solid ${isDark ? '#333' : '#E8E8E8'}`,
              backgroundColor: isDark ? '#1E1E1E' : '#FAFAFA',
              borderRadius: 10,
              padding: '20px 24px',
            }}
          >
            <Skeleton height={12} width="60%" radius="sm" mb={14} />
            <Skeleton height={36} width={180} radius="sm" mb={14} />
            <Skeleton height={10} width="100%" radius="xl" mb={18} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i}>
                  <Skeleton height={11} width="55%" radius="sm" mb={8} />
                  <Skeleton height={22} width="65%" radius="sm" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* What's Causing the Drop-Off (Where to Act) */}
        <div className={classes.section}>
          <Skeleton height={16} width={240} radius="sm" mb={14} />
          <div
            style={{
              border: `1px solid ${isDark ? '#333' : '#E8E8E8'}`,
              backgroundColor: isDark ? '#1E1E1E' : '#FAFAFA',
              borderRadius: 10,
              padding: '20px 24px',
            }}
          >
            <Skeleton height={12} width="70%" radius="sm" mb={12} />
            <Skeleton height={12} width="55%" radius="sm" mb={18} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    border: `1px solid ${isDark ? '#2a2a2a' : '#EFEFEF'}`,
                    borderRadius: 8,
                    padding: '12px 14px',
                  }}
                >
                  <Skeleton height={11} width="50%" radius="sm" mb={8} />
                  <Skeleton height={20} width="60%" radius="sm" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Opportunities */}
        <div className={classes.section}>
          <Skeleton height={16} width={170} radius="sm" mb={14} />
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              style={{
                border: `1px solid ${isDark ? '#333' : '#E8E8E8'}`,
                borderRadius: 10,
                padding: '14px 18px',
                marginBottom: 10,
              }}
            >
              <Skeleton height={12} width="40%" radius="sm" mb={8} />
              <Skeleton height={12} width="80%" radius="sm" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Not loading + no data → empty state. Same three-branch behaviour as
  // BusinessDashboardView (site has campaigns vs not, with vs without tests).
  if (hasNoData) {
    const hasTests = activeTestsCount > 0;
    const testsLine = hasTests
      ? `${activeTestsCount} active test${activeTestsCount === 1 ? '' : 's'} running`
      : 'No active tests running';

    const testsContainer = (
      <div className={classes.emptyTestsRow}>
        <span className={classes.emptyTestsLeft}>
          {hasTests ? <strong>{testsLine}</strong> : testsLine}
          {hasTests ? ' on this site' : ''}
        </span>
        <span className={classes.emptyTestsRight} onClick={onNavigateToTests}>
          View Test Results <IconArrowRight size={12} style={{ verticalAlign: -2 }} />
        </span>
      </div>
    );

    return (
      <div className={classes.root}>
        {siteHasCampaigns ? (
          <>
            <div className={classes.emptyMessage}>
              <p className={classes.emptyMessageTitle}>No data available</p>
              <p className={classes.emptyMessageSub}>
                Select campaigns in Settings to populate this dashboard.
              </p>
              {onOpenSettings && (
                <Button
                  size="xs"
                  variant="light"
                  leftIcon={<IconSettings size={14} />}
                  mt={12}
                  onClick={onOpenSettings}
                >
                  Open Settings
                </Button>
              )}
            </div>
            {testsContainer}
          </>
        ) : hasTests ? (
          <div className={classes.emptyHero}>
            <div className={classes.emptyIcon}>
              <IconFlask size={26} />
            </div>
            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              <p className={classes.emptyTitle}>{testsLine}</p>
              <p className={classes.emptyBody}>
                This site has active tests but no campaigns yet. Review the tests
                currently in flight to see what you're learning.
              </p>
            </div>
            <Button
              variant="filled"
              rightIcon={<IconArrowRight size={14} />}
              onClick={onNavigateToTests}
            >
              Review Tests
            </Button>
          </div>
        ) : (
          <div className={classes.emptyHero}>
            <div className={classes.emptyIcon}>
              <IconFlask size={26} />
            </div>
            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              <p className={classes.emptyTitle}>Nothing running yet on this site</p>
              <p className={classes.emptyBody}>
                Once you launch a campaign or test, results will appear here.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {/* Spec: <30-day warning banner.
          "Date ranges under 30 days may not reflect complete funnel activity.
           Lifecycle events in progress can make performance appear lower than it is." */}
      {selectedDayCount < 30 && (
        <div style={{
          background: isDark ? '#2A2005' : '#FFFBEB',
          border: `1px solid ${isDark ? '#5C4813' : '#FDE68A'}`,
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 14,
          fontSize: 13,
          color: isDark ? 'rgb(245, 158, 11)' : 'rgb(245, 158, 11)',
          lineHeight: 1.5,
        }}>
          Date ranges under 30 days may not reflect complete funnel activity.
          Lifecycle events in progress can make performance appear lower than it is.
        </div>
      )}

      {/* Spec: blue goal-consistency banner — preventative, fires whenever
          more than one campaign is selected (not reactive to actual mismatches). */}
      {campaignCount > 1 && (
        <div style={{
          background: isDark ? '#0C1929' : '#EFF6FF',
          border: `1px solid ${isDark ? '#1E3A5F' : '#BFDBFE'}`,
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 14,
          fontSize: 13,
          color: isDark ? '#93C5FD' : '#1E40AF',
          lineHeight: 1.5,
        }}>
          For accurate funnel reporting, all selected campaigns must share the same goals
          and goal order. If your campaigns use different goals, create a separate dashboard
          scoped to each campaign individually.
        </div>
      )}

      {/* Goal-mismatch warning: campaigns excluded because they don't contain
          the currently-selected Primary Goal. */}
      {skippedCampaigns.length > 0 && (
        <div style={{
          background: isDark ? '#2A2005' : '#FFFBEB',
          border: `1px solid ${isDark ? '#5C4813' : '#FDE68A'}`,
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 14,
          fontSize: 13,
          color: isDark ? 'rgb(245, 158, 11)' : 'rgb(245, 158, 11)',
          lineHeight: 1.5,
        }}>
          <strong>Some campaigns were excluded.</strong>{' '}
          The following campaigns don&apos;t contain the selected Primary Goal ({primaryGoalName}) and
          were not counted: {skippedCampaigns.join(', ')}. Open Settings to pick a goal that exists in every campaign.
        </div>
      )}

      {/* Section 1 — Executive Summary */}
      <ExecutiveSummaryCards
        cards={cards}
        costThreshold={costThreshold}
        isDark={isDark}
        classes={classes}
      />

      {/* Section 2 — Performance Metrics */}
      <PerformanceMetrics
        primary={perfPrimary}
        secondary={perfSecondary}
        isDark={isDark}
        classes={classes}
      />

      {/* Source Distribution */}
      {sources.length > 0 && (
        <SourceDistributionSection
          sources={sources}
          goalName={primaryGoalName}
          isDark={isDark}
          classes={classes}
        />
      )}

      {/* Section 3 — Customer Journey Flow */}
      {journeySteps.length > 0 && constraint && (
        <JourneyFlowSection
          steps={journeySteps}
          constraint={constraint}
          isDark={isDark}
          classes={classes}
          onNavigateToReports={onNavigateToReports}
        />
      )}

      {/* Section 4 — Impact of Improving This Step */}
      {constraint && projection && (
        <ImpactProjectionSection
          constraint={constraint}
          projection={projection}
          projectionPct={projectionPct}
          onProjectionChange={onProjectionChange}
          isDark={isDark}
          classes={classes}
          loading={loading}
        />
      )}

      {/* Section 5 — Where to Act */}
      {constraint && whereToAct && (
        <WhereToActSection
          constraint={constraint}
          whereToAct={whereToAct}
          isDark={isDark}
          classes={classes}
        />
      )}

      {/* Section 6 — Top Opportunities */}
      {opportunities.length > 0 && (
        <TopOpportunitiesSection
          opportunities={opportunities}
          isDark={isDark}
          classes={classes}
          onNavigateToReports={onNavigateToReports}
          onNavigateToTests={onNavigateToTests}
        />
      )}

      {/* Footer */}
      <DashboardFooter
        dateRangeLabel={dateRangeLabel}
        isDark={isDark}
        classes={classes}
        onExportPdf={onExportPdf}
        onShareLink={onShareLink}
      />
    </div>
  );
};

export default MarketingDashboardView;
